import { useEffect, useMemo, useRef, useState } from 'react';
import _ from 'lodash';
import { t, Trans } from '@lingui/macro';
import { FormProvider, useForm } from 'react-hook-form';
import {
    Box,
    Button,
    FormControlLabel,
    LinearProgress,
    MenuItem,
    Radio,
    RadioGroup,
    Typography,
} from '@material-ui/core';

import AdminPage from '../AdminPage';
import Actions from '../../page-framework/actions/Actions';
import NgpForm from '../../form/NgpForm';
import FormSection from '../../form/FormSection';
import NgpSelect from '../../form/NgpSelect';
import NgpTextField from '../../form/NgpTextField';
import NgpPasswordField from '../../form/NgpPasswordField';
import NgpNativeNumberField from '../../form/NgpNativeNumberField';
import { RowFlexLayout } from '../../layout';
import NgpToggle from '../../form/NgpToggle';
import DataTable from '../../data-table/DataTable';
import { useQueryLoader } from '../../data-table/Loaders';
import { useFindById } from '../../../services/FormQueryService';
import { useDataService } from '../../../services/ApiDataService';
import { useFormSaveService, useFormSubmitWrapper } from '../../../services/FormSaveService';
import { usePulseSoftwareVersion } from '../../../stores/EnvironmentStore';
import { AdminPages, buildPageId, useEntityIdParam } from '../AdminLinkUtils';
import { AGENT_TYPE_GLOBAL, AGENT_TYPE_VIRTUAL } from '../agents/AgentConstants';
import {
    AGENT_VOIP_CONFIGURATION_ADAPTER_TYPE_FIELD,
    AGENT_VOIP_MODE_FIELD,
    VOIP_MODE_NONE,
    VOIP_MODE_PRE_MANUAL,
    VOIP_MODE_PRE_PROVISIONED,
} from '../agents-voip/AgentVoIPConstants';
import { buildVersion } from '../agents/AgentDetailHelper';
import { hasWireless, isHardware, isHardware3k } from '../agents/AgentHelper';
import { buildVoIPRegistration } from '../agents-voip/AgentVoIPHelper';
import { CERTIFICATE_USAGES } from '../certificates/CertificateConstants';
import VoipAgentReadOnlySection from './VoipAgentReadOnlySection';
import VoipAgentRegistrationSection from './VoipAgentRegistrationSection';
import {
    VOIP_ADAPTER_TYPE,
    VOIP_ADAPTER_TYPE_OPTIONS,
    VOIP_AGENTS_API_URL,
    VOIP_CALL_MANAGERS_API_URL,
    VOIP_DEFAULT_VALUES,
    VOIP_DSCP_OPTIONS,
    VOIP_HARDWARE_ONLY_MODES,
    VOIP_IP_PRECEDENCE_OPTIONS,
    VOIP_IP_VERSION_OPTIONS,
    VOIP_LIST_URL,
    VOIP_CALL_MANAGER_TYPE_CUCM,
    VOIP_MODE_OPTIONS_ISNG,
    VOIP_MODE_OPTIONS_LEGACY,
    VOIP_PHONE_REGEX,
    VOIP_QOS_MODE,
    VOIP_QOS_MODE_OPTIONS,
    VOIP_ROOT,
    VOIP_TOS_CODE_OPTIONS,
    VOIP_VLAN_STATUS_OPTIONS,
    VOIP_VLAN_ISNG_STATUS_OPTIONS,
} from './VoipConstants';
import {
    certColumns,
    buildQosSelection,
    isManual,
    isAuto,
    isConfigured,
    getCmLabelsForCallManagerType,
    prepareVoipAgentSaveData,
} from './VoipAgentDetailHelper';
import { useDetailStyles } from './VoipCallManagersStyles';
import { USE_FORM_DEFAULT_OPTIONS } from '../../../utilities/FormUtilities';
import useUiToasts, { createToastConfig, INFO } from '../../../stores/ToastStore';
import { useUserId, useUserName } from '../../../stores/SessionStore';
import { runVoipDialToneVerify } from './VoipVerifyHelper';
import VoipKeyValueDetails from './VoipKeyValueDetails';

const INVALID_VALUE_ERROR = t`Invalid value entered`;

export default function VoipAgentDetailPage() {
    const classes = useDetailStyles();
    const recordId = useEntityIdParam();
    const dataService = useDataService();
    const formSaveService = useFormSaveService();
    const { submitWrapper, saving } = useFormSubmitWrapper();
    const [pulseSoftwareVersion] = usePulseSoftwareVersion();
    const [, uiToastActions] = useUiToasts();
    const [userId] = useUserId();
    const [userName] = useUserName();
    const [readOnlyAgent, setReadOnlyAgent] = useState(null);
    const [callManagers, setCallManagers] = useState([]);
    const [qosSelection, setQosSelection] = useState(VOIP_QOS_MODE.default);
    const [verifyLoading, setVerifyLoading] = useState(false);
    const [showVerify, setShowVerify] = useState(false);
    const [verifyResult, setVerifyResult] = useState({ status: '', statusMsg: '', statusHtml: '' });

    const methods = useForm({
        ...USE_FORM_DEFAULT_OPTIONS,
        defaultValues: VOIP_DEFAULT_VALUES,
        mode: 'onChange',
    });
    const { reset, setError, watch, handleSubmit, getValues, setValue, trigger } = methods;

    const postFindByIdHandler = async (agentResponse) => {
        const clonedReadOnlyAgent = _.cloneDeep(agentResponse || {});
        if (pulseSoftwareVersion) {
            buildVersion(clonedReadOnlyAgent, pulseSoftwareVersion);
        }
        buildVoIPRegistration(clonedReadOnlyAgent);
        setReadOnlyAgent(clonedReadOnlyAgent);

        const voipCallManagers = await dataService.get(VOIP_CALL_MANAGERS_API_URL);
        setCallManagers(voipCallManagers || []);
        setQosSelection(buildQosSelection(agentResponse?.voipConfiguration));

        return agentResponse;
    };

    const { initialized, finding, formData } = useFindById({
        url: VOIP_AGENTS_API_URL,
        recordId,
        postFindByIdHandler,
        resetFunc: reset,
        defaultValues: VOIP_DEFAULT_VALUES,
    });

    const voipMode = watch(AGENT_VOIP_MODE_FIELD);
    const watchedVoipConfiguration = watch('voipConfiguration');
    const useDirectCalling = !!watch('voipConfiguration.useDirectCalling');
    const certificates = useMemo(() => _.get(formData, 'certificates', []), [formData]);
    const selectedCallManagerId = watch('voipConfiguration.primaryCallManager');
    const selectedCallManager = useMemo(
        () => callManagers.find((manager) => manager?._id === selectedCallManagerId),
        [callManagers, selectedCallManagerId]
    );
    useEffect(() => {
        if (selectedCallManager?.enableSecurity !== true) {
            setValue('voipConfiguration.enableUnsecureCall', false);
        }
    }, [selectedCallManagerId, selectedCallManager?.enableSecurity, setValue]);
    const cmLabels = getCmLabelsForCallManagerType(selectedCallManager?.cmType);
    const notManual = !isManual(voipMode);
    const adapterType = watch('voipConfiguration.adapterType') || VOIP_ADAPTER_TYPE.default;
    const agentHasWireless = hasWireless(formData);
    const showDirectCallingToggle = isManual(voipMode) && formData?.type !== AGENT_TYPE_GLOBAL;
    // const showWirelessAdapterSelector = agentHasWireless && isConfigured(voipMode);

    const certDataSource = useMemo(() => {
        return {
            url: '/query/admin/nPointCertificate',
            totalProperty: 'count',
            dataProperty: 'data',
            params: {
                query: JSON.stringify({
                    _id: { $in: certificates || [] },
                    certificateUsage: { $eq: CERTIFICATE_USAGES.voip },
                }),
            },
        };
    }, [certificates]);
    const certLoader = useQueryLoader(certDataSource, { disableFilterState: true });

    const verifyTest = async () => {
        await runVoipDialToneVerify({
            dataService,
            trigger,
            getValues,
            formData,
            selectedCallManager,
            qosSelection,
            userName,
            userId,
            setShowVerify,
            setVerifyLoading,
            setVerifyResult,
        });
    };

    const handleFormSubmit = async (submitData = {}) => {
        const putData = prepareVoipAgentSaveData({
            formData,
            submitData,
            callManagers,
            qosSelection,
        });

        return formSaveService.save({
            url: '/ipm/v1/admin/agents',
            recordId,
            dataToSave: _.extend(putData, { _id: recordId }),
            setError,
        });
    };

    const handleSubmitFunction = submitWrapper(handleFormSubmit, VOIP_LIST_URL);

    const title = formData?.name || t`nPoint`;

    const generation = Number(formData?.generation ?? 0);
    const type = formData?.type;
    const isVirtualOrGlobal = type === AGENT_TYPE_GLOBAL || type === AGENT_TYPE_VIRTUAL;
    const voipModeOptions = useMemo(() => {
        const baseOptions = generation > 3 ? VOIP_MODE_OPTIONS_ISNG : VOIP_MODE_OPTIONS_LEGACY;
        return baseOptions.filter((option) => !isVirtualOrGlobal || !VOIP_HARDWARE_ONLY_MODES.includes(option.value));
    }, [generation, isVirtualOrGlobal]);

    const showVlanSection = isHardware(formData) && adapterType !== VOIP_ADAPTER_TYPE.wireless && generation <= 3;
    const showVlanIsngSection = adapterType !== VOIP_ADAPTER_TYPE.wireless && generation > 3;
    const showVlanIdField =
        watch('voipConfiguration.vvlan.mode') === 'M' &&
        isHardware(formData) &&
        adapterType !== VOIP_ADAPTER_TYPE.wireless;

    const previousVoipModeRef = useRef(undefined);
    const voipConfigurationByModeRef = useRef({});

    useEffect(() => {
        if (!initialized || _.isNil(watchedVoipConfiguration)) {
            return;
        }

        // During mode transitions, keep writing to the previous mode bucket until switch handling completes.
        const snapshotMode = previousVoipModeRef.current ?? voipMode;
        if (!isConfigured(snapshotMode)) {
            return;
        }

        voipConfigurationByModeRef.current[snapshotMode] = _.cloneDeep(watchedVoipConfiguration);
    }, [initialized, voipMode, watchedVoipConfiguration]);

    useEffect(() => {
        if (!initialized) {
            return;
        }

        const currentVoipConfiguration = _.cloneDeep(getValues('voipConfiguration'));

        if (previousVoipModeRef.current === undefined) {
            voipConfigurationByModeRef.current[voipMode] = currentVoipConfiguration;
            previousVoipModeRef.current = voipMode;
            return;
        }

        const previousVoipMode = previousVoipModeRef.current;
        if (previousVoipMode === voipMode) {
            return;
        }

        if (previousVoipMode === VOIP_MODE_NONE || !_.isNil(currentVoipConfiguration)) {
            voipConfigurationByModeRef.current[previousVoipMode] = currentVoipConfiguration;
        }

        let nextVoipConfiguration = _.cloneDeep(voipConfigurationByModeRef.current[voipMode] || null);
        if (isManual(voipMode)) {
            nextVoipConfiguration = nextVoipConfiguration || {};
            _.defaults(nextVoipConfiguration, { adapterType: VOIP_ADAPTER_TYPE.default, ipVersion: 'ipv4' });
        }

        const _adapterType = _.get(nextVoipConfiguration, 'adapterType');
        const hasVlanMode = !_.isEmpty(_.get(nextVoipConfiguration, 'vvlan.mode'));
        if (
            nextVoipConfiguration &&
            ((isHardware(formData) && voipMode === VOIP_MODE_PRE_MANUAL && !hasVlanMode) ||
                _adapterType === VOIP_ADAPTER_TYPE.wireless)
        ) {
            _.set(nextVoipConfiguration, 'vvlan.mode', VOIP_MODE_NONE);
        }

        if (voipMode === VOIP_MODE_PRE_PROVISIONED) {
            uiToastActions.createToast(
                createToastConfig(INFO, t`Pre-Provisioned VoIP will need to be setup via the Pulse Web Configuration`)
            );
        }

        if (voipMode === VOIP_MODE_NONE) {
            setShowVerify(false);
        }

        const nextValues = {
            ...getValues(),
            voipConfiguration: nextVoipConfiguration,
        };
        reset(nextValues);

        previousVoipModeRef.current = voipMode;
    }, [initialized, voipMode, formData, getValues, reset, uiToastActions]);

    return (
        <AdminPage
            title={title}
            pageId={buildPageId(AdminPages.VoipConfig)}
            breadcrumbs={[
                { link: VOIP_ROOT, text: t`VoIP Configuration` },
                { link: VOIP_LIST_URL, text: t`nPoint Configuration` },
                { text: title },
            ]}
            actions={
                <Actions>
                    <Button
                        onClick={() => verifyTest()}
                        disabled={verifyLoading || !isManual(voipMode) || useDirectCalling}
                    >
                        <Trans>Verify</Trans>
                    </Button>

                    <Button onClick={handleSubmit(handleSubmitFunction)} variant="contained" color="primary">
                        <Trans>Save</Trans>
                    </Button>
                </Actions>
            }
        >
            <FormProvider {...methods}>
                <NgpForm
                    submitFunction={handleSubmitFunction}
                    initialized={initialized}
                    loading={finding || saving}
                    showSave={false}
                >
                    <VoipAgentReadOnlySection agent={readOnlyAgent || {}} recordId={recordId} />
                    <VoipAgentRegistrationSection
                        registrationDetail={_.get(readOnlyAgent, 'screenFields.registrationDetail', {})}
                    />

                    <FormSection title={<Trans>VoIP Configuration</Trans>}>
                        <NgpSelect
                            className={classes.select}
                            fieldName={AGENT_VOIP_MODE_FIELD}
                            label={t`Configuration`}
                            required
                        >
                            {voipModeOptions.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </NgpSelect>

                        {isConfigured(voipMode) && (
                            <>
                                <NgpSelect
                                    className={classes.select}
                                    fieldName={AGENT_VOIP_CONFIGURATION_ADAPTER_TYPE_FIELD}
                                    label={t`VoIP Interface`}
                                    disabled={isAuto(voipMode) || !agentHasWireless}
                                    onChangeFunc={(event) => {
                                        if (event?.target?.value === VOIP_ADAPTER_TYPE.wireless) {
                                            setValue('voipConfiguration.ipVersion', 'ipv4');
                                        }
                                    }}
                                >
                                    {VOIP_ADAPTER_TYPE_OPTIONS.map((option) => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </NgpSelect>
                                {!agentHasWireless && (
                                    // <Typography variant="body2" color="textSecondary">
                                    <Typography variant="body2">
                                        <Trans>
                                            Note: Interface selection is only supported on wireless capable platforms
                                        </Trans>
                                    </Typography>
                                )}

                                <NgpSelect
                                    className={classes.select}
                                    fieldName="voipConfiguration.ipVersion"
                                    label={t`IP Version`}
                                    disabled={
                                        isAuto(voipMode) ||
                                        (isHardware3k(formData) && adapterType === VOIP_ADAPTER_TYPE.wireless)
                                    }
                                >
                                    {VOIP_IP_VERSION_OPTIONS.map((option) => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </NgpSelect>

                                {showDirectCallingToggle && (
                                    <NgpToggle
                                        fieldName="voipConfiguration.useDirectCalling"
                                        label={<Trans>Use Direct Calling</Trans>}
                                        helpTipText={t`Perform VoIP tests without a call manager`}
                                    />
                                )}

                                {useDirectCalling ? (
                                    <>
                                        <NgpTextField
                                            fieldName="voipConfiguration.phoneNumber"
                                            label={t`Phone Number`}
                                            required={!notManual}
                                            fieldRules={{
                                                pattern: {
                                                    value: VOIP_PHONE_REGEX,
                                                    message: INVALID_VALUE_ERROR,
                                                },
                                            }}
                                            onBlurFunc={() => {
                                                /**
                                                 * Only used for CUCM
                                                 *  We don't show the SIP User ID field on the CUCM detail screen because it's going
                                                 *    to be the same as the phone number, keep it in sync.
                                                 */
                                                const phoneNumber = getValues('voipConfiguration.phoneNumber');
                                                setValue('voipConfiguration.sipUserId', phoneNumber || '');
                                            }}
                                            inputProps={{ maxLength: 20, size: 30 }}
                                        />
                                        <NgpTextField
                                            fieldName="voipConfiguration.sipProxy"
                                            label={t`SIP Proxy's Hostname/IP`}
                                            disabled={notManual}
                                            inputProps={{ maxLength: 65, size: 30 }}
                                        />
                                        <NgpNativeNumberField
                                            fieldName="voipConfiguration.rtpStartPort"
                                            label={t`RTP Start Port`}
                                            min={1}
                                            max={65535}
                                            disabled={notManual}
                                            inputWidth={'20ch'}
                                            helpTipText={t`Default start port is 4000`}
                                        />
                                        <NgpNativeNumberField
                                            fieldName="voipConfiguration.rtpEndPort"
                                            label={t`RTP End Port`}
                                            min={1}
                                            max={65535}
                                            disabled={notManual}
                                            inputWidth={'20ch'}
                                            helpTipText={t`Default end port is 6000`}
                                        />
                                    </>
                                ) : (
                                    <>
                                        <NgpSelect
                                            className={classes.select}
                                            fieldName="voipConfiguration.primaryCallManager"
                                            label={t`Call Manager`}
                                            required={!notManual && !selectedCallManager}
                                            disabled={notManual}
                                        >
                                            {callManagers.map((manager) => (
                                                <MenuItem key={manager._id} value={manager._id}>
                                                    {manager.name}
                                                </MenuItem>
                                            ))}
                                        </NgpSelect>

                                        {selectedCallManager && (
                                            <>
                                                <NgpTextField
                                                    fieldName="voipConfiguration.phoneNumber"
                                                    label={cmLabels.phoneNumber}
                                                    required={
                                                        !notManual &&
                                                        selectedCallManager.cmType === VOIP_CALL_MANAGER_TYPE_CUCM
                                                    }
                                                    fieldRules={{
                                                        pattern: {
                                                            value: VOIP_PHONE_REGEX,
                                                            message: INVALID_VALUE_ERROR,
                                                        },
                                                    }}
                                                    disabled={notManual}
                                                    onBlurFunc={() => {
                                                        if (
                                                            selectedCallManager.cmType === VOIP_CALL_MANAGER_TYPE_CUCM
                                                        ) {
                                                            const phoneNumber = getValues(
                                                                'voipConfiguration.phoneNumber'
                                                            );
                                                            setValue('voipConfiguration.sipUserId', phoneNumber || '');
                                                        }
                                                    }}
                                                    inputProps={{ maxLength: 20, size: 20 }}
                                                />

                                                <NgpTextField
                                                    fieldName="voipConfiguration.extension"
                                                    label={cmLabels.extension}
                                                    fieldRules={{
                                                        pattern: {
                                                            value: VOIP_PHONE_REGEX,
                                                            message: INVALID_VALUE_ERROR,
                                                        },
                                                    }}
                                                    disabled={notManual}
                                                    inputProps={{ maxLength: 20, size: 20 }}
                                                />

                                                {selectedCallManager.cmType !== VOIP_CALL_MANAGER_TYPE_CUCM && (
                                                    <NgpTextField
                                                        fieldName="voipConfiguration.sipUserId"
                                                        label={cmLabels.sipUserId}
                                                        required={!notManual}
                                                        disabled={notManual}
                                                        onBlurFunc={() => {
                                                            const sipUserId = getValues('voipConfiguration.sipUserId');
                                                            const authName = getValues(
                                                                'voipConfiguration.encryptedAuthorizationName.value'
                                                            );
                                                            if (!authName && sipUserId) {
                                                                setValue(
                                                                    'voipConfiguration.encryptedAuthorizationName.value',
                                                                    sipUserId
                                                                );
                                                            }
                                                        }}
                                                        inputProps={{ maxLength: 65, size: 50 }}
                                                    />
                                                )}

                                                <NgpTextField
                                                    fieldName="voipConfiguration.encryptedAuthorizationName.value"
                                                    label={cmLabels.authName}
                                                    required={!notManual}
                                                    disabled={notManual}
                                                    inputProps={{ maxLength: 65, size: 50 }}
                                                />

                                                <NgpPasswordField
                                                    fieldName="voipConfiguration.encryptedPassword.value"
                                                    label={cmLabels.password}
                                                    required={!notManual}
                                                    disabled={notManual}
                                                    inputProps={{ maxLength: 65, size: 50 }}
                                                    togglePasswordVisibility
                                                />

                                                {selectedCallManager?.enableSecurity && (
                                                    <NgpToggle
                                                        fieldName="voipConfiguration.enableUnsecureCall"
                                                        label={<Trans>Place unsecure calls</Trans>}
                                                        helpTipText={t`Call to secure-only phones/nPoints will fail. However, the nPoint will still answer secure calls.`}
                                                    />
                                                )}
                                                {showVlanSection && (
                                                    <NgpSelect
                                                        className={classes.select}
                                                        fieldName="voipConfiguration.vvlan.mode"
                                                        label={t`VLAN Mode`}
                                                        disabled={notManual}
                                                    >
                                                        {VOIP_VLAN_STATUS_OPTIONS.map((option) => (
                                                            <MenuItem key={option.value} value={option.value}>
                                                                {option.label}
                                                            </MenuItem>
                                                        ))}
                                                    </NgpSelect>
                                                )}
                                                {showVlanIsngSection && (
                                                    <NgpSelect
                                                        className={classes.select}
                                                        fieldName="voipConfiguration.vvlan.mode"
                                                        label={t`VLAN Mode`}
                                                        disabled={notManual}
                                                    >
                                                        {VOIP_VLAN_ISNG_STATUS_OPTIONS.map((option) => (
                                                            <MenuItem key={option.value} value={option.value}>
                                                                {option.label}
                                                            </MenuItem>
                                                        ))}
                                                    </NgpSelect>
                                                )}
                                                {showVlanIdField && (
                                                    <NgpNativeNumberField
                                                        fieldName="voipConfiguration.vvlan.vlanId"
                                                        label={t`VLAN ID`}
                                                        min={1}
                                                        max={4094}
                                                        disabled={notManual}
                                                        inputWidth={'20ch'}
                                                    />
                                                )}
                                            </>
                                        )}
                                    </>
                                )}
                            </>
                        )}
                    </FormSection>

                    <FormSection title={<Trans>Associated VoIP Certificates</Trans>}>
                        <div className={classes.certContainer}>
                            <DataTable
                                tableId="voip-agent-certificate-list"
                                loader={certLoader}
                                columns={certColumns}
                                visibleRows={5}
                                disableColumnRemover
                                disableCSVExport
                                emptyText={t`There is no VoIP certificate associated with current nPoint.`}
                            />
                        </div>
                    </FormSection>

                    {isConfigured(voipMode) && selectedCallManager && (
                        <FormSection title={<Trans>Quality of Service</Trans>}>
                            <RadioGroup value={qosSelection} onChange={(event) => setQosSelection(event.target.value)}>
                                {VOIP_QOS_MODE_OPTIONS.map((option) => (
                                    <FormControlLabel
                                        key={option.value}
                                        value={option.value}
                                        label={option.label}
                                        control={<Radio />}
                                        disabled={notManual}
                                    />
                                ))}
                            </RadioGroup>

                            {qosSelection === VOIP_QOS_MODE.dscp && (
                                <NgpSelect
                                    className={classes.select}
                                    fieldName="voipConfiguration.qos.dscpCode"
                                    label={t`DSCP Code`}
                                    disabled={notManual}
                                >
                                    {VOIP_DSCP_OPTIONS.map((option) => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </NgpSelect>
                            )}

                            {qosSelection === VOIP_QOS_MODE.ip && (
                                <>
                                    <NgpSelect
                                        className={classes.select}
                                        fieldName="voipConfiguration.qos.ipPrecedenceCode"
                                        label={t`IP Precedence`}
                                        disabled={notManual}
                                    >
                                        {VOIP_IP_PRECEDENCE_OPTIONS.map((option) => (
                                            <MenuItem key={option.value} value={option.value}>
                                                {option.label}
                                            </MenuItem>
                                        ))}
                                    </NgpSelect>

                                    <NgpSelect
                                        className={classes.select}
                                        fieldName="voipConfiguration.qos.tosCode"
                                        label={t`Type of Service`}
                                    >
                                        {VOIP_TOS_CODE_OPTIONS.map((option) => (
                                            <MenuItem key={option.value || 'none'} value={option.value}>
                                                {option.label}
                                            </MenuItem>
                                        ))}
                                    </NgpSelect>
                                </>
                            )}
                        </FormSection>
                    )}

                    {showVerify && (
                        <FormSection title={<Trans>Verify</Trans>}>
                            {verifyLoading && <LinearProgress />}
                            <div className={classes.keyValueContainer}>
                                <Typography variant="h6">{verifyResult.status}</Typography>
                                <Typography variant="body2">{verifyResult.statusMsg}</Typography>
                                {verifyResult.statusHtml && (
                                    <Box mt={1}>
                                        <VoipKeyValueDetails details={verifyResult.statusHtml} />
                                    </Box>
                                )}
                            </div>
                        </FormSection>
                    )}

                    <RowFlexLayout mt={8} gridGap={10}>
                        <Button
                            size={'large'}
                            onClick={handleSubmit(handleSubmitFunction)}
                            variant="contained"
                            color="primary"
                        >
                            <Trans>Save</Trans>
                        </Button>

                        <Button
                            size={'large'}
                            onClick={() => verifyTest()}
                            disabled={verifyLoading || !isManual(voipMode) || useDirectCalling}
                        >
                            <Trans>Verify</Trans>
                        </Button>
                    </RowFlexLayout>
                </NgpForm>
            </FormProvider>
        </AdminPage>
    );
}
